import React, { useCallback, useState, useEffect } from "react";
import { callApi } from "utils/ContentoApi";
import { useSelector, useDispatch } from "react-redux";

import SearchPage from "./pages/SearchPage";
import ResultPage from "./pages/ResultPage";
import LoadingSpinner from "./components/LoadingSpinner";
import { computeInitialView } from "./utils";
import { useCreateGeneration } from "./useCreateGeneration";
import { setCfPage, setCfTab } from "../../state/actions/ContentFactoryActions";
import { ContentFactoryState } from "../../state/reducers/ContentFactoryReducer";

interface AccountState {
  data: { id: string };
}

interface RootState {
  account: AccountState;
  contentFactory: ContentFactoryState;
}

const ContentFactoryPage: React.FC = () => {
  const dispatch = useDispatch();
  const account = useSelector((state: RootState) => state.account.data);
  // The page + tab live in the store so navigating away and back restores them.
  const page = useSelector((state: RootState) => state.contentFactory.currentPage);
  const tab = useSelector((state: RootState) => state.contentFactory.currentTab);

  const [initialPendingBatchId, setInitialPendingBatchId] = useState<string | null>(null);

  const { createBatch } = useCreateGeneration(account.id);

  const initView = useCallback(async () => {
    try {
      const [batchesResponse, profilesResponse] = await Promise.all([
        callApi({ method: "get", url: `content-factory/accounts/${account.id}/postBatches` }, true, true),
        callApi({ method: "get", url: `content-factory/accounts/${account.id}/profiles` }, true, true),
      ]);
      // The endpoint already excludes acknowledged failures, so any returned batch
      // (generating, done, or a not-yet-acknowledged failure) lands on the posts tab.
      const hasBatch = (batchesResponse?.postBatches ?? []).length > 0;
      const hasProfiles = (profilesResponse?.profiles ?? []).length > 0;
      const view = computeInitialView({ hasBatch, hasProfiles });
      if (view.page === "result") dispatch(setCfTab(view.tab));
      dispatch(setCfPage(view.page, ""));
    } catch (err) {
      dispatch(setCfPage("search", ""));
      console.error("Failed to initialize content factory view", { accountId: account.id, err });
    }
  }, [account.id, dispatch]);

  // Only compute the landing view on first entry; later entries restore the
  // remembered page + tab from the store.
  useEffect(() => {
    if (page === null) initView();
  }, [page, initView]);

  // The search page only generates from a URL. On success, jump to the posts tab
  // with the new batch already pending so its card shows immediately.
  const handleSearchSubmit = useCallback(async (url: string) => {
    const postBatchId = await createBatch({ type: "url", url });
    if (postBatchId) {
      setInitialPendingBatchId(postBatchId);
      dispatch(setCfTab("posts"));
      dispatch(setCfPage("result", ""));
    }
  }, [createBatch, dispatch]);

  if (page === null) {
    return <LoadingSpinner />;
  }

  if (page === "search") {
    return <SearchPage onSubmit={handleSearchSubmit} />;
  }

  return (
    <ResultPage
      accountId={account.id}
      initialTab={tab ?? "profiles"}
      initialPendingBatchId={initialPendingBatchId}
    />
  );
};

export default ContentFactoryPage;
